import type { AxiosInstance } from 'axios';

import Vue from 'vue';

import { formatPhone } from '@/utils/phone';

import { profileGet } from '@/api/endpoints/user-company';

const investorPendingStates = ['irf_checked', 'irf_considering', 'irf_autocheck_failed', 'irf_autocheck_success', 'iru_checked', 'iru_considering', 'iru_autocheck_failed', 'iru_autocheck_success', 'iru_fin_pass_err', 'irf_fin_pass_err'];

// fixme: в перспективе это все должно быть изолировано и не экспортироваться, на экпорт только публичный апи
export type TaskmanagerTaskType = 'investor_reg_pp' | 'investor_reg_le' | 'invest_profile_config_pp' | 'invest_profile_config_le' | 'invest_qual_achieve' | 'borrower_reg' | 'borrower_declaration' | 'borrower_profile_config';
export type CompanyType = 'invest' | 'invest.FL' | 'invest.IP' | 'invest.UL' | 'borr' | 'borr.IP' | 'borr.UL';
export type CompanyDocumentType = 'user-avatar' | 'investor_profile_avatar' | 'qi:depositories_reports' | 'qi:foreign_reports' | 'qi:person_statements_reports' | 'qi:broker_reports' | 'passport1' | string;
export interface CompanyDocument {
  id: string
  type: CompanyDocumentType
  link: string
}
export interface CompanyServerData {
  id: string
  name?: string
  inn?: string
  ogrn?: string
  type?: CompanyType
  docs: CompanyDocument[]
  taskmanager_task_id: string
  is_qualified_investor?: boolean
  referral_code?: string
  founders?: {
    type?: string
    name?: string
    id: string
    docs: CompanyDocument[]
  }[]
}

export interface UserServerData {
  userid: string
  phone: string
  tasks: {
    id: string
    state: 'create' | 'pending' | 'bank-confirm' | 'done'
    type: TaskmanagerTaskType
    last_task_state: string
    dadd: string
  }[]
  docs: CompanyDocument[]
  pin_settings?: {
    isQuickAccessOn: boolean
    hasPinSet: boolean
  }
  companys: CompanyServerData[]
  active_sessions?: number
  total_unread_tickets?: number
  pass_change?: string
  isAuthenticated: boolean
}

const innerStore = defineStore('user-data', () => {
  const http = Vue.http as AxiosInstance;

  const isLoading = ref<boolean>(false);

  const data = shallowRef<UserServerData | null>(null);
  const companyId = ref<string | null>(null);

  const phone = computed(() => data.value?.phone);

  const selectedCompany = computed(() => {
    const entry = data.value?.companys?.find((c) => c.id === companyId.value);
    if (!entry)
      return null;

    return {
      name: entry.name || formatPhone(phone.value!),
      isIP: entry.type?.toLowerCase().endsWith('ip'),
      isFL: entry.type?.toLowerCase().endsWith('fl'),
      isUL: entry.type?.toLowerCase().endsWith('ul'),
      isInvestor: entry.type?.toLowerCase().includes('invest'),
      isBorrower: entry.type?.toLowerCase().includes('borr'),
      userAvatar: entry.docs?.find((d) => d.type === 'user-avatar'),
      leaderOrFounderName: entry.founders?.find(({ type }) => type?.includes('leader'))?.name ?? entry.founders?.find(({ type }) => type?.includes('founder'))?.name,
    };
  });

  const name = computed(() => {
    if (!data.value)
      return undefined;

    if (!selectedCompany.value)
      return formatPhone(phone.value!);

    return selectedCompany.value?.name || formatPhone(phone.value!);
  });
  const avatarCachedURL = ref<string | null>(null);
  watch(selectedCompany, (nv) => {
    if (!nv)
      return;

    if (avatarCachedURL.value)
      URL.revokeObjectURL(avatarCachedURL.value);

    const avatarSF = nv.userAvatar;

    if (!avatarSF?.link) {
      avatarCachedURL.value = null;
      return;
    }
    return fetch(avatarSF.link)
      .then((r) => r.blob())
      .then((blob) => {
        avatarCachedURL.value = URL.createObjectURL(blob);
      });
  });

  const investorData = computed(() => data.value?.companys?.find((c) => c.type?.includes('invest')));
  const borrowerData = computed(() => data.value?.companys?.find((c) => c.type?.includes('borr')));
  const selectedCompanyData = computed(() => data.value?.companys?.find((c) => c.id === companyId.value));

  const dateLastPwdChange = computed(() => data.value?.pass_change ? new Date(data.value?.pass_change) : undefined);

  const investor = computed(() => {
    const entry = investorData.value;

    if (!entry)
      return;

    const isFL = entry.type?.toLowerCase().endsWith('fl');

    const tmTask = data.value!.tasks?.find((t) => t.id === entry.taskmanager_task_id)
      ?? data.value!.tasks?.find((t) => (isFL && t.type === 'investor_reg_pp') || (!isFL && t.type === 'investor_reg_le')); // тут эта тупость потому, что taskmanager_task_id появляется не после создания в таске, а после прохождения первого шага реги;
    const state: 'create' | 'pending' | 'bank-confirm' | 'done' = (() => {
      if (!tmTask)
        return 'create';

      const lastTaskState = tmTask.last_task_state;

      if (investorPendingStates.includes(lastTaskState))
        return 'pending';

      if (['irf_waitcoin', 'iru_waitcoin'].includes(lastTaskState))
        return 'bank-confirm';

      return tmTask.state;
    })();

    return {
      name: entry.name || formatPhone(phone.value!),
      isIP: entry.type?.toLowerCase().endsWith('ip'),
      isFL,
      isUL: entry.type?.toLowerCase().endsWith('ul'),
      isAccredited: entry.is_qualified_investor,
      mayBecomeAccredited: entry.type?.toLowerCase().endsWith('fl') && !entry.is_qualified_investor,
      registrationState: state,
      registrationTask: tmTask,
      referralCode: entry.referral_code,
      investProfileTask: data.value?.tasks
        ?.filter((t) => ['invest_profile_config_le', 'invest_profile_config_pp'].includes(t.type))
        .sort((b, a) => new Date(a.dadd).getTime() - new Date(b.dadd).getTime())[0] // fixme: потенциально слабое место, надо договориться с бэком чтобы отдавал только последнюю таску?
    };
  });

  const borrower = computed(() => {
    const entry = borrowerData.value;

    if (!entry)
      return;

    const tmTask = data.value!.tasks?.find((t) => t.id === entry.taskmanager_task_id)
      ?? data.value!.tasks?.find((t) => t.type === 'borrower_reg'); // тут эта тупость потому, что taskmanager_task_id появляется не после создания в таске, а после прохождения первого шага реги
    const state: 'create' | 'pending' | 'bank-confirm' | 'done' = (() => {
      if (!tmTask)
        return 'create';

      return tmTask.state;
    })();

    return {
      name: entry.name || formatPhone(phone.value!),
      isIP: entry.type?.toLowerCase().endsWith('ip'),
      isUL: entry.type?.toLowerCase().endsWith('ul'),
      registrationState: state,
      registrationTask: tmTask,
      borrowerProfileTask: data.value?.tasks
        ?.filter((t) => t.type === 'borrower_profile_config')
        .sort((b, a) => new Date(a.dadd).getTime() - new Date(b.dadd).getTime())[0] // fixme: потенциально слабое место, надо договориться с бэком чтобы отдавал только последнюю таску?
    };
  });

  const activeSessionsCount = computed(() => data.value?.active_sessions);
  const unreadTickets = computed(() => data.value?.total_unread_tickets);

  async function update() {
    isLoading.value = true;
    await http.get(profileGet)
      .then(({ data: rawData }) => {
        data.value = rawData.response;
      })
      .finally(() => {
        isLoading.value = false;
      });
  }

  function reset() {
    data.value = null;
  }

  async function resetAfter(callback: CallableFunction) {
    isLoading.value = true;
    await callback();
    reset();
    isLoading.value = false;
  }

  function setCompanyId(payload: string | null) {
    companyId.value = payload;
  }

  return {
    isLoading: readonly(isLoading),

    data: readonly(data),

    selectedCompanyData,
    name,
    avatarCachedURL,

    investorData,
    borrowerData,
    phone,
    activeSessionsCount,
    dateLastPwdChange,
    unreadTickets,

    investor,
    borrower,
    selectedCompany,

    update,
    reset,
    resetAfter,
    setCompanyId,
  };
});

export interface UseProfileDataOptions {
  init: boolean
}

const defaultOptions = { init: false };

export function useProfileData(options: UseProfileDataOptions = defaultOptions) {
  const { init } = options;

  const store = innerStore();

  if (init && !store.isLoading)
    store.update();

  return store;
}
